import { IconPlus, IconX } from "@tabler/icons-react";
import { Button, IconButton, Input, Switch, Text } from "@/components/ui";
import type { Notificacion, VariableDeEvento } from "../../../services/notificaciones-service";
import { segmentosSms } from "../../../utils/sms";
import { CampoPlantilla } from "./CampoPlantilla";

interface Props {
  notificacion: Notificacion;
  variables: VariableDeEvento[];
  onCambio: (cambio: Partial<Notificacion>) => void;
}

/** SMS · texto plano; muestra cuántos segmentos cobra Twilio (con el texto de la plantilla, sin sustituir variables). */
export function ContenidoSms({ notificacion: n, variables, onCambio }: Props) {
  const texto = n.texto ?? "";
  const { segmentos, unicode } = segmentosSms(texto);
  return (
    <CampoPlantilla
      etiqueta="Texto del SMS"
      valor={texto}
      onCambio={(t) => onCambio({ texto: t })}
      variables={variables}
      multilinea
      filas={4}
      ayuda={`${texto.length} caracteres · ≈ ${segmentos} segmento(s)${unicode ? " (con acentos/emoji cada segmento admite 67)" : ""}. Las variables cambian la longitud final.`}
    />
  );
}

/** WhatsApp · plantilla aprobada por Meta: Content SID + valor de cada marcador {{1}}, {{2}}… */
export function ContenidoWhatsApp({ notificacion: n, variables, onCambio }: Props) {
  const mapeo = n.variablesWhatsApp ?? {};
  const marcadores = Object.keys(mapeo).sort((a, b) => Number(a) - Number(b));
  const cambiarMapeo = (m: Record<string, string>) => onCambio({ variablesWhatsApp: m });

  return (
    <>
      <Input
        label="Content SID de la plantilla"
        size="xs"
        placeholder="HX0123456789abcdef0123456789abcdef"
        value={n.contentSid ?? ""}
        onChange={(e) => onCambio({ contentSid: e.target.value.trim() })}
        description="Se crea y aprueba en Twilio/Meta fuera del portal. Para mensajes iniciados por la empresa no hay texto libre."
      />
      <div className="space-y-2">
        <Text size="xs" fw={500}>Marcadores de la plantilla</Text>
        {marcadores.map((m) => (
          <div key={m} className="flex items-start gap-1.5">
            <span className="mt-6 w-10 shrink-0 font-mono text-xs text-muted-foreground">{`{{${m}}}`}</span>
            <div className="flex-1">
              <CampoPlantilla etiqueta={`Valor de {{${m}}}`} valor={mapeo[m]} onCambio={(v) => cambiarMapeo({ ...mapeo, [m]: v })} variables={variables} />
            </div>
            <IconButton
              size="xs"
              className="mt-6"
              aria-label={`Quitar marcador ${m}`}
              onClick={() => {
                const { [m]: _quitado, ...resto } = mapeo;
                cambiarMapeo(resto);
              }}
            >
              <IconX />
            </IconButton>
          </div>
        ))}
        <Button
          size="xs"
          variant="default"
          leftSection={<IconPlus size={12} />}
          onClick={() => cambiarMapeo({ ...mapeo, [String((marcadores.map(Number).at(-1) ?? 0) + 1)]: "" })}
        >
          Agregar marcador
        </Button>
        <Text size="xs" c="dimmed">Deben coincidir en número y orden con los marcadores de la plantilla aprobada.</Text>
      </div>
    </>
  );
}

/** Telegram · texto con MarkdownV2 opcional (los valores de las variables se escapan solos). */
export function ContenidoTelegram({ notificacion: n, variables, onCambio }: Props) {
  return (
    <>
      <CampoPlantilla
        etiqueta="Mensaje"
        valor={n.texto ?? ""}
        onCambio={(t) => onCambio({ texto: t })}
        variables={variables}
        multilinea
        filas={5}
        ayuda={n.usarMarkdown ? "MarkdownV2: *negritas*, _cursivas_. En el texto fijo escapa con \\ los caracteres . - ( ) ! si no son formato." : undefined}
      />
      <Switch
        label="Usar formato MarkdownV2"
        checked={!!n.usarMarkdown}
        onCheckedChange={(usarMarkdown) => onCambio({ usarMarkdown })}
      />
    </>
  );
}

/** Web push · título, cuerpo y ruta del portal que se abre al hacer clic. */
export function ContenidoWebPush({ notificacion: n, variables, onCambio }: Props) {
  return (
    <>
      <CampoPlantilla etiqueta="Título" valor={n.titulo ?? ""} onCambio={(titulo) => onCambio({ titulo })} variables={variables} />
      <CampoPlantilla etiqueta="Cuerpo" valor={n.cuerpo ?? ""} onCambio={(cuerpo) => onCambio({ cuerpo })} variables={variables} multilinea filas={3} />
      <CampoPlantilla
        etiqueta="Enlace al hacer clic (opcional)"
        valor={n.enlace ?? ""}
        onCambio={(enlace) => onCambio({ enlace: enlace || null })}
        variables={variables}
        placeholder="/solicitudes/{{SolicitudId}}"
        ayuda="Ruta del portal que empiece con /. Llega a los navegadores donde el usuario activó las notificaciones."
      />
    </>
  );
}
